import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from '../../../../redux/store';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import { useParams } from 'react-router-dom';
import { Chip, Container, Grid, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography } from '@mui/material';
import Logo from '../../../../components/Logo';
import moment from 'moment';
import { getSalaryById } from '../../../../redux/slices/hrRedux/UserManagementRedux/salaryRedux';
import { safeJSONParse } from '../../../../utils/common';
import { fCurrency } from '../../../../utils/formatNumber';
import Iconify from '../../../../components/Iconify';

// import html2pdf from 'html2pdf.js';

export default function PaySheetIndex() {
  const { token } = useParams();
  const { themeStretch } = useSettings();
  const [sheetData, setSheetData] = useState({});
  const dispatch = useDispatch();

  const { data: rawData } = useSelector((state) => state.salary);

  useEffect(() => {
    if (token) {
      dispatch(getSalaryById(token));
    }
  }, [token, dispatch]);

  useEffect(() => {
    const salaryRecord = rawData?.[0];
    if (salaryRecord?.data) {
      const parsedData = safeJSONParse(salaryRecord.data);
      console.log(parsedData);
      let month = salaryRecord.month;
      if (!month && parsedData?.workingDayData?.rostDays?.[0]) {
        month = parsedData.workingDayData.rostDays[0];
      } else if (!month) {
        month = new Date().toISOString();
      }
      setSheetData({ ...parsedData, month });
    }
    // If your rawData is directly the object (for testing with the provided JSON sample):
    // else if (
  }, [rawData]);

  const handleDownload = () => {
    if (window.html2pdf) {
      const element = document.getElementById('paySheet');
      const opt = {
        margin: 0.5,
        filename: `PaySheet_${sheetData?.userData?.name}_${moment(sheetData?.month).format('MMMM_YYYY')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true }, // useCORS might be needed for images
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      };
      window.html2pdf().from(element).set(opt).save();
    } else {
      console.error('html2pdf.js is not loaded. Please ensure the library is included in your project.');
      alert('PDF download functionality is currently unavailable.');
    }
  };

  const styling = {
    amountStyles: {
      fontWeight: '600',
      fontSize: '0.9em',
      textAlign: 'right',
    },
    customTooltip: {
      backgroundColor: '#f5f5f9',
      color: '#333',
      maxWidth: 220,
      border: '1px solid #dadde9',
      fontSize: '0.875rem',
      boxShadow: 2,
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      gap: '5px',
      maxHeight: '200px',
      overflowY: 'auto',
    },
    finalAmountStyles: {
      fontWeight: '800',
      fontSize: '1.1em',
      textAlign: 'right',
    },
    pointer: {
      cursor: 'pointer',
    },
    headerCell: {
      fontWeight: '700',
      fontSize: '0.9em',
    },
    subHeaderCell: {
      fontWeight: 'normal',
      fontSize: '0.85em',
      paddingLeft: '25px !important',
    },
    highlightNegative: {
      color: '#d32f2f',
    },
    categoryHeader: {
      fontSize: '1.1em',
      fontWeight: '700',
      marginTop: '15px',
      marginBottom: '8px',
      borderBottom: '1px solid #ddd',
      paddingBottom: '4px',
    },
    tableRowHighlight: {
      backgroundColor: '#f5f5f5',
    },
  };

  const contractualBasic = sheetData?.userData?.salary_basic || 0;
  const actualBasicPaid = sheetData?.basicSalary?.amount || 0;
  const noPayDaysCount = sheetData?.workingDayData?.noPayDays?.length || sheetData?.noPayDeduction?.days?.length || 0;

  const totalBasicAndBRA = sheetData?.totalBasicSalaryForEPFETF || 0;
  const totalOtherAllowances = sheetData?.totalAllowance || 0;
  const grossEarningsProvisional = totalBasicAndBRA + totalOtherAllowances;

  const explicitNoPayRelatedDeduction = sheetData?.noPayDeduction?.amount || 0;
  const systemGrossSalary = sheetData?.grossSalary || 0;

  // const otherAdjustments = grossEarningsProvisional - explicitNoPayRelatedDeduction - systemGrossSalary;

  const totalStatutoryDeductions = (sheetData?.deductions?.epf?.employee || 0) + (sheetData?.deductions?.payeTax || 0);

  if (!sheetData || Object.keys(sheetData).length === 0 || !sheetData.userData) {
    return (
      <Page title='Pay Sheet: Loading...'>
        <Container>
          <Typography>Loading pay sheet data...</Typography>
        </Container>
      </Page>
    );
  }

  const prettifyCamelCase = (str) => {
    if (!str) return '';
    const result = str.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  return (
    <Page title={`Pay Sheet: ${sheetData?.userData?.name || ''} - ${moment(sheetData?.month).format('MMMM YYYY')}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <Stack direction='row' justifyContent='flex-end' sx={{ mb: 2 }}>
          <Tooltip title='Download PDF' onClick={handleDownload}>
            <IconButton>
              <Iconify icon={'eva:download-fill'} />
            </IconButton>
          </Tooltip>
        </Stack>
        <div id='paySheet' style={{ padding: '20px', width: '100%', border: '1px solid #eaeaea', background: '#fff' }}>
          <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
            <Grid item>
              <Logo sx={{ width: 70, height: 70 }} />
            </Grid>
            <Grid item xs>
              <Typography variant='h4' component='p' sx={{ textAlign: 'center', fontWeight: 'bold', textDecoration: 'underline' }}>
                SALARY PAY SHEET
              </Typography>
              <Typography variant='h6' component='p' sx={{ textAlign: 'center', fontWeight: 'medium' }}>
                For {moment(sheetData?.month).format('MMMM YYYY')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' }, mt: { xs: 1, sm: 0 } }}>
              <Typography variant='body2'>
                <strong>Date Issued:</strong> {moment().format('DD MMMM YYYY')}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography style={styling.categoryHeader}>Company Details</Typography>
              <Typography variant='body2'>
                <b>Bubble Mania (PVT) Ltd</b>
                <br />
                No. 101/A5, New Kandy Road,
                <br />
                Kothalawala, Kaduwela
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography style={styling.categoryHeader}>Employee Details</Typography>
              <Typography variant='body2'>
                <b>{sheetData?.userData?.name}</b>
                <br />
                EPF Number: {sheetData?.userData?.epfNumber}
                <br />
                Designation: {sheetData?.userData?.designation}
                <br />
                Address: {sheetData?.userData?.address}, {sheetData?.userData?.city}
                <br />
                Phone: {sheetData?.userData?.phoneNumber}
                <br />
                Email: {sheetData?.userData?.email}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography style={styling.categoryHeader}>Salary Details</Typography>
              <TableContainer>
                <Table size='small'>
                  <TableBody>
                    <TableRow>
                      <TableCell style={styling.headerCell}>Number of Roster Working Days</TableCell>
                      <TableCell style={styling.amountStyles}>{sheetData?.noOfDaysShouldWork || 'N/A'} Days</TableCell>
                    </TableRow>
                    {noPayDaysCount > 0 && (
                      <TableRow>
                        <TableCell style={{ ...styling.headerCell, ...styling.highlightNegative }}>No-Pay Days</TableCell>
                        <TableCell style={{ ...styling.amountStyles, ...styling.highlightNegative }}>{noPayDaysCount} Day(s)</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography style={styling.categoryHeader}>Earnings</Typography>
              <TableContainer>
                <Table size='small'>
                  <TableBody>
                    <Tooltip style={styling.pointer} title={'This is your basic salary!'} placement='top-start'>
                      <TableRow>
                        <TableCell style={styling.headerCell}>Contractual Basic Salary</TableCell>
                        <TableCell style={styling.amountStyles}>{fCurrency(contractualBasic)}</TableCell>
                      </TableRow>
                    </Tooltip>
                    {sheetData?.noPayDeduction?.amount > 0 && (
                      <Tooltip
                        style={styling.pointer}
                        componentsProps={{ tooltip: { sx: styling.customTooltip } }}
                        title={
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            {sheetData?.workingDayData?.noPayDays?.map((value, index) => (
                              <Chip key={index} label={value} size='small' />
                            ))}
                          </div>
                        }
                        placement='top-start'
                      >
                        <TableRow>
                          <TableCell style={{ ...styling.subHeaderCell, ...styling.highlightNegative }}> Less: No-Pay Deduction from Basic</TableCell>
                          <TableCell style={{ ...styling.amountStyles, ...styling.highlightNegative }}>({fCurrency(sheetData?.noPayDeduction?.amount)})</TableCell>
                        </TableRow>
                      </Tooltip>
                    )}
                    <TableRow>
                      <TableCell style={styling.headerCell}>Actual Basic Salary Paid</TableCell>
                      <TableCell style={styling.amountStyles}>{fCurrency(actualBasicPaid)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={styling.headerCell}>Budgetary Relief Allowance One</TableCell>
                      <TableCell style={styling.amountStyles}>{fCurrency(sheetData?.budgetReliefAllowance1)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={styling.headerCell}>Budgetary Relief Allowance Two</TableCell>
                      <TableCell style={styling.amountStyles}>{fCurrency(sheetData?.budgetReliefAllowance2)}</TableCell>
                    </TableRow>
                    <TableRow sx={styling.tableRowHighlight}>
                      <TableCell sx={{ ...styling.headerCell, fontWeight: 'bold' }}>Total Salary for EPF/ETF</TableCell>
                      <TableCell sx={{ ...styling.amountStyles, fontWeight: 'bold' }}>{fCurrency(totalBasicAndBRA)}</TableCell>
                    </TableRow>
                    {Object.entries(sheetData?.allowance || {}).map(([key, value]) => {
                      if (value?.amount && value.amount !== 0) {
                        let componentForToolTip = <></>;
                        switch (key) {
                          case 'ot':
                            componentForToolTip = (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {value?.data?.map((otData) => {
                                  return (
                                    <p style={{ fontSize: 10 }}>
                                      <sub>{otData?.date}</sub> : <b>{fCurrency(otData?.otPay)}</b> ({otData?.timeDiffInMinutes} Minutes)
                                    </p>
                                  );
                                })}
                              </div>
                            );
                            break;
                          case 'attendanceAllowance':
                            componentForToolTip = (
                              <div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: '5px' }}>
                                {value?.leaveDates?.lateDays?.length > 0 && (
                                  <>
                                    <p>
                                      <u>Late Days</u>
                                    </p>
                                    {value?.leaveDates?.lateDays?.map((dates, index) => {
                                      return <Chip key={`QuickUpdate${index}`} label={dates?.logTime} size='small' />;
                                    })}
                                  </>
                                )}
                                {value?.leaveDates?.noPayDays?.length > 0 && (
                                  <>
                                    <p>
                                      <u>No Pay Leaves</u>
                                    </p>{' '}
                                    {value?.leaveDates?.noPayDays?.map((dates, index) => {
                                      return <Chip key={index} label={dates} size='small' />;
                                    })}
                                  </>
                                )}
                              </div>
                            );
                            break;
                          case 'targetAllowance':
                            componentForToolTip = (
                              <div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: '5px' }}>
                                <p>
                                  You have got {value?.workingDayPercentage * 100}% from the total of {value?.salesTargetAmount}
                                </p>
                              </div>
                            );
                            break;
                          case 'fixAllowance':
                            componentForToolTip = (
                              <div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: '5px' }}>
                                <p>
                                  You have got {value?.workingDayPercentage * 100}% from the total of {value?.fixedAllowanceAllowed}
                                </p>
                              </div>
                            );
                            break;
                          case 'commissionAllowance':
                            componentForToolTip = (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {value?.data?.map((value) => {
                                  return (
                                    <p style={{ fontSize: 10 }}>
                                      <sub>{value?.date}</sub> : <b>{fCurrency(value?.commission)}</b> ({fCurrency(value?.totalSalesAmount)} Total Sale)
                                    </p>
                                  );
                                })}
                              </div>
                            );
                            break;
                          case 'inspectionAllowance':
                            componentForToolTip = (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {value?.data?.map((value) => {
                                  return (
                                    <p style={{ fontSize: 10 }}>
                                      <sub>
                                        {value?.name} : <b>{Math.round(value?.averageMark)} %</b> ({fCurrency(value?.amount)})
                                      </sub>
                                    </p>
                                  );
                                })}
                              </div>
                            );
                            break;
                          case 'performanceAllowance':
                            componentForToolTip = (
                              <div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: '5px' }}>
                                <p>
                                  You have got {value?.workingDayPercentage * 100}% from the total of {value?.fixedAllowanceAllowed}
                                </p>
                              </div>
                            );
                            break;
                        }

                        return (
                          <Tooltip style={styling.pointer} componentsProps={{ tooltip: { sx: styling.customTooltip } }} title={componentForToolTip} placement='top-start'>
                            <TableRow key={key}>
                              <TableCell style={styling.headerCell}>{prettifyCamelCase(key)}</TableCell>
                              <TableCell style={styling.amountStyles}>{fCurrency(value.amount)}</TableCell>
                            </TableRow>
                          </Tooltip>
                        );
                      }
                      return null;
                    })}
                    <TableRow sx={styling.tableRowHighlight}>
                      <TableCell sx={{ ...styling.headerCell, fontWeight: 'bold' }}>Total Other Allowances</TableCell>
                      <TableCell sx={{ ...styling.amountStyles, fontWeight: 'bold' }}>{fCurrency(totalOtherAllowances)}</TableCell>
                    </TableRow>
                    {/*<TableRow>*/}
                    {/*  <TableCell sx={{ ...styling.headerCell, fontWeight: 'bold', fontSize: '1em' }}>GROSS EARNINGS (Provisional)</TableCell>*/}
                    {/*  <TableCell sx={{ ...styling.amountStyles, fontWeight: 'bold', fontSize: '1em' }}>{fCurrency(grossEarningsProvisional)}</TableCell>*/}
                    {/*</TableRow>*/}
                  </TableBody>
                </Table>
              </TableContainer>

              <TableContainer sx={{ mt: 1, borderTop: '1px solid #ccc' }}>
                <Table size='small'>
                  <TableBody>
                    <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                      <TableCell sx={{ ...styling.headerCell, fontSize: '1em', fontWeight: 'bold' }}>GROSS SALARY</TableCell>
                      <TableCell sx={{ ...styling.amountStyles, fontSize: '1em', fontWeight: 'bold' }}>{fCurrency(systemGrossSalary)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography style={styling.categoryHeader}>Statutory Deductions</Typography>
              <TableContainer>
                <Table size='small'>
                  <TableBody>
                    <TableRow>
                      <TableCell style={styling.headerCell}>Employee EPF (8%)</TableCell>
                      <TableCell style={{ ...styling.amountStyles, ...styling.highlightNegative }}>({fCurrency(sheetData?.deductions?.epf?.employee)})</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={styling.headerCell}>PAYE Tax</TableCell>
                      <TableCell style={{ ...styling.amountStyles, ...styling.highlightNegative }}>({fCurrency(sheetData?.deductions?.payeTax)})</TableCell>
                    </TableRow>
                    <TableRow sx={styling.tableRowHighlight}>
                      <TableCell sx={{ ...styling.headerCell, fontWeight: 'bold' }}>Total Statutory Deductions</TableCell>
                      <TableCell sx={{ ...styling.amountStyles, fontWeight: 'bold', ...styling.highlightNegative }}>({fCurrency(totalStatutoryDeductions)})</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <TableContainer sx={{ mt: 2, borderTop: '2px solid black', borderBottom: '2px solid black' }}>
                <Table size='small'>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ ...styling.headerCell, fontSize: '1.2em', fontWeight: 'bold' }}>NET SALARY</TableCell>
                      <TableCell sx={{ ...styling.finalAmountStyles, fontSize: '1.2em', fontWeight: 'bold' }}>{fCurrency(sheetData?.netSalary)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography style={styling.categoryHeader}>Company Contributions</Typography>
              <TableContainer>
                <Table size='small'>
                  <TableBody>
                    <TableRow>
                      <TableCell style={styling.headerCell}>Employer EPF (12%)</TableCell>
                      <TableCell style={styling.amountStyles}>{fCurrency(sheetData?.deductions?.epf?.company)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={styling.headerCell}>Employer ETF (3%)</TableCell>
                      <TableCell style={styling.amountStyles}>{fCurrency(sheetData?.deductions?.etf?.company)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          <Grid container sx={{ mt: 4 }}>
            <Grid item xs={6} sx={{ textAlign: 'center' }}>
              <Typography variant='body2' sx={{ mb: 4 }}>
                _________________________
              </Typography>
              <Typography variant='body2'>Employee Signature</Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: 'center' }}>
              <Typography variant='body2' sx={{ mb: 4 }}>
                _________________________
              </Typography>
              <Typography variant='body2'>For Bubble Mania (PVT) Ltd</Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant='caption'>This is a computer-generated document.</Typography>
          </Grid>
        </div>
      </Container>
    </Page>
  );
}
